import { DataResult, process, State } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  ComboBox,
  ComboBoxChangeEvent,
  DropDownList,
  DropDownListChangeEvent
} from '@progress/kendo-react-dropdowns';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useSessionStorage } from 'usehooks-ts';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import {
  CustomColumnImage,
  CustomColumnPlainText,
  CustomColumnTextarea,
  CustomColumnVideo
} from '../../../components/grid/custom-column';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import { NoRecords } from '../../../components/grid/no-records';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import { GameDocumentContext } from '../../../contexts/game-document';
import {
  ResourceWindow,
  UploadedImage
} from '../../../features/game-document/image-resource/resource-window';
import { ResourcePackEditorWindow } from '../../../features/game-document/resource-packs/resource-pack-editor-window';
import { ResourcePackHtmlEditorWindow } from '../../../features/game-document/resource-packs/resource-pack-html-editor-window';
import { YesNoDialogResult } from '../../../types/dialog-result';
import { GameDocument } from '../../../types/game-document';
import { ResourcePackEntity } from '../../../types/game-document/entities';
import { LanguageMapping } from '../../../types/game-document/entities/resource-pack';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import {
  GetResourceEntity,
  GetResourceValue,
  GetTaskContentById,
  UpdateGameDocState,
  UpdateResourceAsync,
  UpdateTaskContentAsync,
  isSystemDefaultResource
} from '../../../utils/game-document';
import {
  AddResourcePackAsync,
  AddResourcePackResourceAsync,
  GetResourcePackByName,
  GetResourcePackResource,
  GetResourcePacks,
  UpdateResourcePackDetailAsync,
  UpdateResourcePackResourceAsync
} from '../../../utils/game-document/resource-packs';
import Toolbar from '../toolbar';
import { useGameDocumentResources } from '../../../hooks/use-game-document-resources';
import {
  DisplayLanguagePublished,
  GetDisplayLanguagePublished
} from '../../../services/admin-display-language';
import { languageOrder } from '../../../utils/game-document/language-order';
import { appStore } from '../../../stores/app-store';
import { useTranslationProgress } from '../../../hooks/use-translation-progress';
import { Col, Row } from 'react-bootstrap';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Label } from '@progress/kendo-react-labels';
import cloneDeep from 'lodash.clonedeep';
import { matchPath, useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { uuid } from '../../../types/common-helper';
import CustomColumnVideoPlatform from '../../../components/grid/custom-column/custom-column-video-platform';

type selectedResource = {
  isOriginal?: boolean;
  resourceId?: string;
  type?: string;
  value?: string;
  additionalProps?: Record<string, any>;
};

const categoryOrder = [
  'Overview',
  'Maps',
  'Zones',
  'Tasks',
  'Task Contents',
  'Inventory',
  'Timers',
  'Titles',
  'Settings'
];

const customSort = (a: any, b: any) => {
  const aIndex = categoryOrder.indexOf(a.category);
  const bIndex = categoryOrder.indexOf(b.category);

  if (aIndex === bIndex) {
    return (a.subcategory ?? '').localeCompare(b.subcategory ?? '');
  }
  return aIndex - bIndex;
};

const defaultImage = {
  defaultFeedbackAllCorrectIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/checked.png',
  defaultFeedbackAllWrongIconResId:
    'https://stsharedappstorage.blob.core.windows.net/prod-catalyst-files/cross.png'
};

const customFeedbackImageUrl = (
  gameDocument: GameDocument,
  res: 'defaultFeedbackAllCorrectIconResId' | 'defaultFeedbackAllWrongIconResId'
) => {
  if (gameDocument.settings && gameDocument.settings.designer) {
    const feedbackIcon = GetResourceValue(
      gameDocument,
      gameDocument.settings.designer[res]
    );

    return feedbackIcon !== '' ? feedbackIcon : defaultImage[res];
  } else {
    return res === 'defaultFeedbackAllCorrectIconResId'
      ? defaultImage.defaultFeedbackAllCorrectIconResId
      : defaultImage.defaultFeedbackAllWrongIconResId;
  }
};

const Language = () => {
  const {
    onProgress,
    startProgress,
    stopProgress,
    renderTranslationProgressDialog
  } = useTranslationProgress();
  const initialDataState: State = {
    ...DefaultGridSettings.initialDataState,
    group: [
      { field: 'category', compare: customSort },
      {
        field: 'subcategory'
      }
    ]
  };
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useState<State>(initialDataState);
  const resources = useGameDocumentResources();
  const [languages, setLanguages] = useState<LanguageMapping[]>([]);
  const [showSystemDefaults, setShowSystemDefaults] = useSessionStorage<boolean>(
    'show-system-default-resources',
    false
  );
  const [displayLanguages, setDisplayLanguages] = useState<
    DisplayLanguagePublished[]
  >([]);
  const [entityEditorValue, setEntityEditorValue] =
    useState<EntityEditor<ResourcePackEntity>>();
  const [entityEditorIsVisible, setEntityEditorIsVisible] =
    useState<boolean>(false);
  const toggleEntityEditor = () => {
    setEntityEditorIsVisible(!entityEditorIsVisible);
  };
  const [languageList, setLanguageList] = useState<string[]>();
  const [currentLocale, setCurrentLocale] = useState<string>();
  const [uploadMediaIsVisible, setUploadMediaIsVisible] =
    useState<boolean>(false);
  const [htmlEditorVisible, setHtmlEditorVisible] = useState<boolean>(false);
  const [selectedResource, setSelectedResource] = useState<selectedResource>();
  const [showDeleteLanguageConfirm, setShowDeleteLanguageConfirm] =
    useState<boolean>(false);
  const [listedLanguages, setListedLanguages] = useState<string[]>([]);
  const [listedDisplayLanguages, setListedDisplayLanguages] = useState<
    string[]
  >([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>();

  const toggleUploadMediaEditor = () => {
    setUploadMediaIsVisible(!uploadMediaIsVisible);
  };

  const toggleHtmlEditor = () => {
    setHtmlEditorVisible(!htmlEditorVisible);
  };

  let entity: ResourcePackEntity = {
    id: '',
    name: '',
    description: '',
    resources: []
  };

  const onAddEntity = () => {
    setEntityEditorValue({
      isNew: true,
      entity
    });

    toggleEntityEditor();
  };

  const onEditEntity = () => {
    let resourcePacks = GetResourcePacks(state.gameDocument!);

    let resourcePacksIndex = resourcePacks.findIndex(
      (i) => i.name === currentLocale
    )!;

    if (resourcePacksIndex > -1) {
      let currentEntity = resourcePacks[resourcePacksIndex];
      setEntityEditorValue({
        isNew: false,
        entity: {
          id: currentEntity.id,
          description: currentEntity.description,
          resources: currentEntity.resources,
          name: currentEntity.name,
          displayLanguage: currentEntity.displayLanguage,
          displayLanguageUrl: currentEntity.displayLanguageUrl
        }
      });
    }

    toggleEntityEditor();
  };

  const handleUploadMediaSubmit = (uploadedMedia: UploadedImage) => {
    // no selected resource. exit early.
    if (!selectedResource) return;

    // no uploaded media. exit early.
    if (!uploadedMedia) return;

    // update custom feedback in Task Content form
    if (
      selectedResource.additionalProps?.taskContentId &&
      selectedResource.additionalProps?.formId
    ) {
      const taskContent = GetTaskContentById(
        state.gameDocument,
        selectedResource.additionalProps?.taskContentId
      );
      if (taskContent && taskContent.forms) {
        const formIdx = taskContent.forms.findIndex(
          (f) => f.id === selectedResource.additionalProps?.formId
        );
        if (formIdx !== -1 && taskContent.forms[formIdx].customFeedback) {
          if (uploadedMedia.resource) {
            taskContent.forms[formIdx].customFeedback[
              (selectedResource.resourceId ?? '') as 'allWrongUrlResId'
            ] = uploadedMedia.resource.id;
          } else {
            resources.addResourceFile(uuid(), uploadedMedia, (res) => {
              taskContent.forms![formIdx].customFeedback![
                (selectedResource.resourceId ?? '') as 'allWrongUrlResId'
              ] = res.id;
            });
          }

          UpdateTaskContentAsync(
            state.gameDocument!,
            selectedResource.additionalProps.taskContentId,
            taskContent
          ).then((updatedGameDocument) => {
            setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
          });
        }
      }
    }

    // save the original resource
    if (selectedResource.isOriginal)
      resources.updateResourceFile(selectedResource.resourceId!, uploadedMedia);
    // save a resource pack resource
    else if (currentLocale && currentLocale !== '')
      resources.updateResourcePackFile(
        currentLocale,
        selectedResource.resourceId!,
        uploadedMedia
      );

    toggleUploadMediaEditor();
  };

  const handleHtmlEditorSubmit = (value: string) => {
    updateResourceHandler(
      selectedResource?.isOriginal!,
      selectedResource?.resourceId!,
      value!
    );

    toggleHtmlEditor();
  };

  const updateResourceValue = (resourceId: string, value: string) => {
    let resource = GetResourceEntity(state?.gameDocument!, resourceId);

    if (resource) {
      resource.value = value;
      UpdateResourceAsync(state?.gameDocument!, resource?.id, resource).then(
        (response) => {
          setState((prevState) => UpdateGameDocState(prevState, response));
        }
      );
    }
  };

  const updateResourcePackValue = (resourceId: string, value: string) => {
    if (currentLocale && currentLocale !== '') {
      let resource = GetResourcePackResource(
        state?.gameDocument!,
        currentLocale,
        resourceId
      );

      if (resource) {
        resource.value = value;
        UpdateResourcePackResourceAsync(
          state?.gameDocument!,
          currentLocale,
          resource.id,
          resource
        ).then((response) => {
          setState((prevState) => UpdateGameDocState(prevState, response!));
        });
      }

      if (!resource) {
        let originalResource = GetResourceEntity(
          state?.gameDocument!,
          resourceId
        );

        AddResourcePackResourceAsync(
          state?.gameDocument!,
          currentLocale,
          originalResource!
        ).then((response) => {
          setState((prevState) => UpdateGameDocState(prevState, response!));
        });
      }
    }
  };

  const updateResourceHandler = (
    isOriginal: boolean,
    resourceId: string,
    value: string
  ) => {
    if (isOriginal) {
      // Update the original resource on GameDocument.resources
      updateResourceValue(resourceId, value);
    }
    if (!isOriginal) {
      //Update the resource packs on GameDocument.resourcePacks
      updateResourcePackValue(resourceId, value);
    }
  };

  const handleEntityEditorSubmit = async (
    editorEntity: EntityEditor<ResourcePackEntity>,
    isCopyOriginal?: boolean,
    isTranslateUsingAI?: boolean,
    displayLanguage?: string
  ) => {
    const selectedDisplayLanguage = displayLanguages.find(
      (i) => i.languageName === displayLanguage
    );
    if (editorEntity.isNew) {
      try {
        if (isTranslateUsingAI) {
          startProgress();
        }
        const response = await AddResourcePackAsync(
          state?.gameDocument!,
          editorEntity?.entity?.name,
          isCopyOriginal,
          isTranslateUsingAI,
          selectedDisplayLanguage?.languageName,
          selectedDisplayLanguage?.blobFileUrl,
          isTranslateUsingAI ? onProgress : undefined
        );

        setState((prevState) => UpdateGameDocState(prevState, response));
        setCurrentLocale(editorEntity?.entity?.name);
      } catch (error) {
        console.error(error);
        stopProgress();
      } finally {
        if (isTranslateUsingAI) {
          // stopProgress is handled by the onProgress 'completed' callback
        }
      }
    } else {
      /* Updates the resource pack's detail in the Game Document.
       * Not Update resource.
       */
      UpdateResourcePackDetailAsync(
        state.gameDocument!,
        editorEntity.entity.name,
        editorEntity.entity.id,
        selectedDisplayLanguage!.languageName!,
        selectedDisplayLanguage!.blobFileUrl!
      ).then((response) => {
        setState((prevState) => UpdateGameDocState(prevState, response));
        setCurrentLocale(editorEntity?.entity?.name);
      });
    }

    toggleEntityEditor();
  };

  const populateResources = () => {
    if (state?.gameDocument) {
      const map = GetResourcePackByName(
        state.gameDocument,
        currentLocale ?? ''
      ).filter((f) => f.type !== 'audio');

      const orderedLanguageIds = languageOrder(state.gameDocument);
      const languageMapWithLabel = orderedLanguageIds.map((lang: any) => ({
        id: lang.id,
        ...map.find((m) => m.id === lang.id),
        ...([
          'allCorrectUrlResId',
          'partiallyCorrectUrlResId',
          'allWrongUrlResId'
        ].includes(lang.id) && {
          type: 'image',
          taskContentId: lang.taskContentId,
          formId: lang.formId
        }),
        label: lang.label,
        category: lang.category,
        //@ts-ignore
        subcategory: lang?.subcategory ?? '',
        //@ts-ignore
        bodyType: lang?.bodyType ?? ''
      }));
      setLanguages(languageMapWithLabel);
    }
  };

  const handleUploadMedia = (
    isOriginal: boolean,
    resourceId: string,
    type: 'image' | 'video',
    additionalProps?: Record<string, any>
  ) => {
    setSelectedResource({
      isOriginal: isOriginal,
      resourceId: resourceId,
      type: type,
      additionalProps
    });

    toggleUploadMediaEditor();
  };

  const handleHtmlEditor = (
    isOriginal: boolean,
    resourceId: string,
    type: 'Markdown' | 'Html'
  ) => {
    let value: string = '';

    if (isOriginal) {
      value = GetResourceValue(state?.gameDocument!, resourceId);
    }

    if (!isOriginal) {
      value =
        GetResourcePackResource(
          state?.gameDocument!,
          currentLocale!,
          resourceId
        )?.value ?? '';
    }

    setSelectedResource({
      isOriginal: isOriginal,
      resourceId: resourceId,
      type: type,
      value: value
    });

    toggleHtmlEditor();
  };

  const CustomCell = (isOriginal: boolean, props: GridCellProps) => {
    const resource = languages?.find((x) => x.id === props.dataItem.id);

    if (!isOriginal && (!currentLocale || currentLocale === '')) return <></>;

    if (resource?.type === 'image') {
      const url = isOriginal
        ? resource?.originalLanguage
        : resource?.newLanguage;
      const isDefaultFeedback = [
        'allCorrectUrlResId',
        'partiallyCorrectUrlResId',
        'allWrongUrlResId'
      ].includes(props.dataItem.id);
      const defaultFeedbackUrl = (id: string) => {
        if (id === 'allCorrectUrlResId')
          return customFeedbackImageUrl(
            state.gameDocument!,
            'defaultFeedbackAllCorrectIconResId'
          );

        if (id === 'allWrongUrlResId')
          return customFeedbackImageUrl(
            state.gameDocument!,
            'defaultFeedbackAllWrongIconResId'
          );

        return '/checked.png';
      };

      return (
        <CustomColumnImage
          columnClass={'w-50'}
          containerClass={
            'd-flex flex-column gap-1 justify-content-between w-full p-2'
          }
          label={props.dataItem.label}
          url={isDefaultFeedback ? defaultFeedbackUrl(props.dataItem.id) : url}
          id={resource?.id}
          onEdit={(id) =>
            handleUploadMedia(isOriginal, id!, 'image', {
              ...(props.dataItem.taskContentId && {
                taskContentId: props.dataItem.taskContentId
              }),
              ...(props.dataItem.formId && {
                formId: props.dataItem.formId
              })
            })
          }
          maxHeight={100}
        />
      );
    }

    if (resource?.type === 'video') {
      return (
        <CustomColumnVideo
          columnClass={'w-50'}
          containerClass={
            'd-flex flex-column gap-1 justify-content-between w-full p-2'
          }
          label={props.dataItem.label}
          url={isOriginal ? resource?.originalLanguage : resource?.newLanguage}
          id={resource?.id}
          onEdit={(id) => handleUploadMedia(isOriginal, id!, 'video')}
          maxHeight={100}></CustomColumnVideo>
      );
    }

    if (resource?.type === 'text') {
      return (
        <CustomColumnTextarea
          columnClass={'w-50'}
          containerClass={
            'd-flex flex-column gap-1 justify-content-between w-full p-2'
          }
          label={resource.label}
          id={`${isOriginal ? 'original' : ''}-${resource?.id}`}
          onChange={(id, value) =>
            updateResourceHandler(isOriginal, resource?.id!, value!)
          }
          text={isOriginal ? resource?.originalLanguage : resource?.newLanguage}
          rows={1}
          autoSize
        />
      );
    }

    if (resource?.type === 'text-long') {
      return (
        <CustomColumnTextarea
          columnClass={'w-50'}
          id={resource?.id}
          containerClass={'d-flex justify-content-center w-100 p-2'}
          onChange={(id, value) =>
            updateResourceHandler(isOriginal, id!, value!)
          }
          text={
            isOriginal ? resource?.originalLanguage : resource?.newLanguage
          }></CustomColumnTextarea>
      );
    }

    if (resource?.type === 'text-md') {
      const isHTML =
        props.dataItem.bodyType && props.dataItem.bodyType === 'HTML';
      return (
        <CustomColumnPlainText
          columnClass={'w-50'}
          id={resource?.id}
          onEdit={(id) =>
            handleHtmlEditor(isOriginal, id!, isHTML ? 'Html' : 'Markdown')
          }
          containerClass={'d-flex justify-content-between w-full p-2'}
          text={resource.label ?? 'Markdown'}></CustomColumnPlainText>
      );
    }

    if (resource?.type === 'text-html') {
      return (
        <CustomColumnPlainText
          columnClass={'w-50'}
          id={resource?.id}
          onEdit={(id) => handleHtmlEditor(isOriginal, id!, 'Html')}
          containerClass={'d-flex justify-content-between w-full p-2'}
          text={resource.label ?? 'HTML'}></CustomColumnPlainText>
      );
    }

    if (['youtube', 'vimeo'].includes(resource?.type ?? '')) {
      return (
        <CustomColumnVideoPlatform
          type={resource?.type as 'youtube'}
          videoId={
            (isOriginal ? resource?.originalLanguage : resource?.newLanguage) ??
            ''
          }
        />
      );
    }

    return (
      <CustomColumnPlainText
        columnClass={'w-50'}
        containerClass={'d-flex justify-content-between w-full p-2'}
        text={isOriginal ? resource?.originalLanguage : resource?.newLanguage}
      />
    );
  };

  const onDeleteHandler = () => {
    setShowDeleteLanguageConfirm(true);
  };

  const onConfirmDeleteLanguage = (result: YesNoDialogResult) => {
    if (result === 'yes') {
      const newGameDocument = {
        ...state.gameDocument,
        resourcePacks: state.gameDocument?.resourcePacks?.filter(
          (resourcePack) => resourcePack.name !== currentLocale
        )
      } as GameDocument;

      setState((prevState) => UpdateGameDocState(prevState, newGameDocument));
      setCurrentLocale('');
    }
    setShowDeleteLanguageConfirm(false);
  };

  const languageSelectedEvent = (event: DropDownListChangeEvent) => {
    setCurrentLocale(event.value);
  };

  const defaultLanguageChangedEvent = async (event: ComboBoxChangeEvent) => {
    const newLanguage = event.value;

    if (!state?.gameDocument?.overview) {
      return;
    }

    // Add the original resource to the resource packs
    const addedResourcePack = await AddResourcePackAsync(
      state?.gameDocument!,
      state?.gameDocument!.overview?.languages as string,
      true,
      false,
      state?.gameDocument!.overview?.displayLanguages,
      state?.gameDocument!.overview?.displayLanguagesUrl
    );

    // Get the selected language pack
    let newLanguagePack = addedResourcePack.resourcePacks?.find(
      (pack) => pack.name === newLanguage
    );

    if (!newLanguagePack) {
      return;
    }

    // Get the default resource
    const currentDefaultResources = state.gameDocument.resources || [];

    // Create a copy of the selected language pack
    let updatedPackResources = [...(newLanguagePack.resources || [])];

    // Create Set from ID resource
    const packResourceIds = new Set(updatedPackResources.map((res) => res.id));

    // Sync the resource if any missing resource on the new one
    currentDefaultResources.forEach((mainResource) => {
      if (!packResourceIds.has(mainResource.id)) {
        updatedPackResources.push(mainResource);
      }
    });

    // Update the newLanguagePack with synchronized pack
    newLanguagePack = {
      ...newLanguagePack,
      resources: updatedPackResources
    };

    const finalResourcePacks = addedResourcePack.resourcePacks?.filter(
      (pack) => pack.name !== newLanguage
    );

    let overview = cloneDeep(addedResourcePack.overview);
    const oldDefaultLanguage = overview!.languages;
    overview!.languages = newLanguagePack?.name;
    overview!.displayLanguages = newLanguagePack?.displayLanguage;
    overview!.displayLanguagesUrl = newLanguagePack?.displayLanguageUrl;

    const finalGameDocument: GameDocument = {
      ...addedResourcePack,
      overview: overview,
      resources: newLanguagePack.resources!,
      resourcePacks: finalResourcePacks
    };

    // Update the game document state
    setState((prevState) => UpdateGameDocState(prevState, finalGameDocument));

    // Set the default language UI
    setDefaultLanguage(newLanguage);
    setCurrentLocale(oldDefaultLanguage);
  };

  const populateLanguages = () => {
    if (state.gameDocument) {
      let newLanguages: string[] = [];
      state.gameDocument.resourcePacks?.forEach((resourcePack) => {
        newLanguages.push(resourcePack?.name);
      });

      setLanguageList(newLanguages);
      // for add language combobox
      if (
        state.gameDocument.overview &&
        state.gameDocument.overview.languages
      ) {
        let index = newLanguages.indexOf(state.gameDocument.overview.languages);

        if (index === -1) {
          setListedLanguages(
            newLanguages.concat(state.gameDocument.overview.languages)
          );
        } else {
          setListedLanguages(newLanguages);
        }
      } else {
        setListedLanguages(newLanguages);
      }

      // for add display language combobox
      if (
        state.gameDocument.overview &&
        state.gameDocument.overview.displayLanguages
      ) {
        let indexDisplayLanguages = newLanguages.indexOf(
          state.gameDocument.overview.displayLanguages
        );

        if (indexDisplayLanguages === -1) {
          setListedDisplayLanguages(
            newLanguages.concat(state.gameDocument.overview.displayLanguages)
          );
        } else {
          setListedDisplayLanguages(newLanguages);
        }
      } else {
        setListedDisplayLanguages(newLanguages);
      }

      if (currentLocale === undefined || currentLocale === '') {
        setCurrentLocale(newLanguages[0]);
      }

      setDefaultLanguage(state.gameDocument.overview?.languages);
    }
  };

  const preprocessGroupedData = (data: any[], dataState: State): DataResult => {
    const withSub = data.filter((item) => item.subcategory !== '');
    const withoutSub = data.filter((item) => item.subcategory === '');

    const groupedWithSub = process(withSub, dataState).data;
    const groupedWithoutSub = process(withoutSub, {
      ...dataState,
      group: [dataState.group![0]]
    }).data;

    const combined = mergeGroupedData(groupedWithoutSub, groupedWithSub);

    return {
      data: combined,
      total: data.length
    };
  };

  const mergeGroupedData = (data1: any[], data2: any[]): any[] => {
    const map = new Map<string, any>();

    [...data1, ...data2].forEach((groupItem) => {
      const key = groupItem.value;
      if (!map.has(key)) {
        map.set(key, { ...groupItem, items: [...groupItem.items] });
      } else {
        map.get(key).items.push(...groupItem.items);
      }
    });

    return Array.from(map.values());
  };

  useEffect(() => {
    GetDisplayLanguagePublished().then((res) => {
      setDisplayLanguages(res.data);
    });
  }, []);

  useEffect(() => {
    // Set the page title.

    if (state?.gameDocument) {
      document.title = `Language - ${GetResourceValue(
        state.gameDocument,
        state.gameDocument.overview?.titleResId!
      )}`;
      populateLanguages();
      populateResources();
    }
  }, [state]);

  useEffect(() => {
    if (currentLocale !== '') {
      populateResources();
    }
    populateLanguages();
  }, [currentLocale]);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    let match = matchPath(
      '/designer/:gameId/resources/language/*',
      location.pathname
    );
    if (match) {
      setBreadcrumb([{ text: 'Language', to: match.pathnameBase }]);
    }

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  useEffect(() => {
    populateLanguages();
  }, []);

  const visibleLanguages = useMemo(() => {
    if (showSystemDefaults) {
      return languages;
    }
    return languages.filter((item: any) => {
      if (item.category === 'Settings') return false;
      if (isSystemDefaultResource(item, state.gameDocument)) return false;
      return true;
    });
  }, [languages, showSystemDefaults, state.gameDocument]);

  return (
    <>
      <Toolbar
        title={'Language'}
        notificationTitle={'Language'}
        buttonHelpSupport={{
          title: 'Language and culture guide',
          url: 'https://forum.catalystglobal.com/t/4161'
        }}>
        {entityEditorIsVisible && (
          <ResourcePackEditorWindow
            toggleDialog={toggleEntityEditor}
            onClose={toggleEntityEditor}
            editorEntity={entityEditorValue!}
            editorMode={'basic'}
            onSubmit={handleEntityEditorSubmit}
            isLanguageCombobox={true}
            listedLanguages={listedLanguages}
            allowAddNew
          />
        )}
      </Toolbar>
      <Row className={'mt-2 gy-2'}>
        <Col sm={6} md={4} xs={12}>
          <FieldWrapper>
            <Label>Default language</Label>
            <ComboBox
              value={defaultLanguage}
              onChange={defaultLanguageChangedEvent}
              data={listedLanguages}
            />
          </FieldWrapper>
        </Col>
      </Row>
      <div className={'pt-2'}>
        <GridToolBar
          searchPlaceholder={'Search items'}
          columnsToSearch={[
            'category',
            'subcategory',
            'newLanguage',
            'originalLanguage'
          ]}
          showCardMode={false}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <div className={'d-flex align-items-center ms-2'}>
            <Checkbox
              label={'Show system defaults'}
              checked={showSystemDefaults}
              onChange={(e) => setShowSystemDefaults(Boolean(e.value))}
            />
          </div>
        </GridToolBar>

        <Grid
          pageable={DefaultGridSettings.pagerSettings}
          data={preprocessGroupedData(visibleLanguages, dataState)}
          dataItemKey={'id'}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          groupable={{ enabled: false }}>
          <Column
            field={'originalLanguage'}
            cells={{
              headerCell: () => (
                <th
                  className="k-table-th k-header fw-bold d-flex align-items-center"
                  style={{
                    height: '30px'
                  }}>
                  Original
                </th>
              ),
              data: (props) => CustomCell(true, props)
            }}
          />
          <Column
            field={'newLanguage'}
            cells={{
              headerCell: () => (
                <th className="k-table-th k-header p-1">
                  <div className="d-flex justify-content-between gap-2">
                    <DropDownList
                      className={'bg-white fw-bold'}
                      value={currentLocale}
                      onChange={languageSelectedEvent}
                      data={languageList}
                    />
                    {languageList && languageList.length > 0 && (
                      <Button
                        title={'Delete'}
                        onClick={onEditEntity}
                        themeColor={'secondary'}
                        size={'medium'}
                        className={'medium'}
                        fillMode={'flat'}>
                        <span className="material-symbols-outlined">edit</span>
                      </Button>
                    )}
                    {languageList && languageList.length > 0 && (
                      <Button
                        title={'Delete'}
                        onClick={onDeleteHandler}
                        themeColor={'error'}
                        size={'medium'}
                        className={'medium'}
                        fillMode={'flat'}>
                        <span className="material-symbols-outlined">
                          {'delete'}
                        </span>
                      </Button>
                    )}
                    <Button onClick={onAddEntity} themeColor={'success'}>
                      + Create new
                    </Button>
                  </div>
                </th>
              ),
              data: (props) => CustomCell(false, props)
            }}
          />

          <GridNoRecords>
            <NoRecords />
          </GridNoRecords>
        </Grid>
      </div>
      {uploadMediaIsVisible && (
        <ResourceWindow
          toggleDialog={toggleUploadMediaEditor}
          onSubmit={handleUploadMediaSubmit}
          fileType={[selectedResource?.type as 'image' | 'video']}
          title={`Upload ${selectedResource?.type}`}
          onClose={(e) => toggleUploadMediaEditor()}
          resourceId={selectedResource?.resourceId ?? ''}
        />
      )}
      {htmlEditorVisible && (
        <ResourcePackHtmlEditorWindow
          toggleDialog={toggleHtmlEditor}
          text={selectedResource?.value}
          type={selectedResource?.type as any}
          onSubmit={handleHtmlEditorSubmit}
          onClose={toggleHtmlEditor}
        />
      )}
      {showDeleteLanguageConfirm && (
        <YesNoDialog
          title="Confirm removal"
          onConfirm={onConfirmDeleteLanguage}
          onClose={() => setShowDeleteLanguageConfirm(false)}>
          Are you sure you want to remove the language?
        </YesNoDialog>
      )}
      {renderTranslationProgressDialog()}
    </>
  );
};

export default Language;
